/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from 'react'
import { Table, Form } from 'react-bootstrap'
import { Card } from 'react-bootstrap-v5'
import { Link } from 'react-router-dom'
import Select from 'react-select'
import { toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { getRequestOptions, postRequestOptions } from '../helpers/Fetchwrapper'
import { numberFormat, formatDecimalQuantity } from '../helpers/GlobalHelper'
import GetPagePermission from '../components/GetPagePermission'

interface ProductionRow {
	id: number
	idPackingOrders: number
	packingDate: string
	packingOrderNo: string
	productionOrderNo: string
	batchNo: string | null
	projectName: string
	warehouseName: string | null
	idItems: number
	itemName: string
	idProducts: number
	productName: string
	productCode: string | null
	packing: string | null
	size: string | null
	unitName: string
	bomName: string | null
	noOfPacking: string
	quantity: string
	costPerUnit: string
	totalCost: string
	createdBy: string
}

interface SummaryRow {
	key: string
	label: string
	subLabel: string
	unitName: string
	orders: number
	noOfPacking: number
	quantity: number
	totalCost: number
}

type ViewMode = 'details' | 'product' | 'date'

const allOption = { label: 'All', value: '' }

const viewOptions: { label: string; value: ViewMode }[] = [
	{ label: 'Details', value: 'details' },
	{ label: 'Product wise summary', value: 'product' },
	{ label: 'Date wise summary', value: 'date' },
]

const toInputDate = (date: Date) => {
	const month = String(date.getMonth() + 1).padStart(2, '0')
	const day = String(date.getDate()).padStart(2, '0')
	return `${date.getFullYear()}-${month}-${day}`
}

const toNumber = (value: any) => {
	const num = parseFloat(value)
	return isNaN(num) ? 0 : num
}

const packSize = (row: ProductionRow) => {
	const size = row.size ? `${formatDecimalQuantity(row.size)} ${row.unitName}`.trim() : ''
	return [row.packing, size].filter(Boolean).join(' - ')
}

const PackingProductionReport: React.FC = () => {
	const hasPermission = GetPagePermission('/packing-production-report')

	const today = new Date()
	const [formData, setFormData] = useState({
		idProjects: '',
		idWarehouses: '',
		idItems: '',
		fromDate: toInputDate(new Date(today.getFullYear(), today.getMonth(), 1)),
		toDate: toInputDate(today),
		search: '',
	})

	const [projects, setProjects] = useState<any[]>([])
	const [warehouses, setWarehouses] = useState<any[]>([])
	const [items, setItems] = useState<any[]>([])
	const [project, setProject] = useState<any>(allOption)
	const [warehouse, setWarehouse] = useState<any>(allOption)
	const [item, setItem] = useState<any>(allOption)
	const [view, setView] = useState(viewOptions[0])

	const [loading, setLoading] = useState(false)
	const [loaded, setLoaded] = useState(false)
	const [rows, setRows] = useState<ProductionRow[]>([])

	useEffect(() => {
		fetch(process.env.REACT_APP_API_URL + 'lead/projects', getRequestOptions())
			.then((resp) => resp.json())
			.then((resp) => {
				setProjects([allOption].concat((resp.data || []).map((row: any) => ({ label: row.name, value: row.id }))))
			})
			.catch((error) => {
				console.log(error, 'catch the hoop')
			})

		fetch(process.env.REACT_APP_API_URL + 'reports/packing-production-report/filters', getRequestOptions())
			.then((resp) => resp.json())
			.then((resp) => {
				setWarehouses([allOption].concat((resp.data?.warehouses || []).map((row: any) => ({ label: row.name, value: row.id }))))
				setItems([allOption].concat((resp.data?.items || []).map((row: any) => ({ label: row.name, value: row.id }))))
			})
			.catch((error) => {
				console.log(error, 'catch the hoop')
			})
	}, [])

	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = event.target
		setFormData({ ...formData, [name]: value })
	}

	const handleViewReport = async () => {
		setLoading(true)
		try {
			const response = await fetch(
				process.env.REACT_APP_API_URL + 'reports/packing-production-report',
				postRequestOptions(formData)
			)
			const data = await response.json()

			if (data.success) {
				setRows(data.data.rows ?? [])
				setLoaded(true)
			} else {
				setRows([])
				if (data.errors) {
					for (const i in data.errors) {
						toast(<strong>{data.errors[i].join(' ')}</strong>, {
							type: 'error',
							position: 'bottom-right',
							autoClose: 4000,
						})
					}
				}
			}
		} catch (error) {
			console.error('Error loading report:', error)
			setRows([])
		} finally {
			setLoading(false)
		}
	}

	const totals = useMemo(() => {
		return {
			orders: new Set(rows.map((row) => row.idPackingOrders)).size,
			noOfPacking: rows.reduce((sum, row) => sum + toNumber(row.noOfPacking), 0),
			quantity: rows.reduce((sum, row) => sum + toNumber(row.quantity), 0),
			totalCost: rows.reduce((sum, row) => sum + toNumber(row.totalCost), 0),
		}
	}, [rows])

	const summaryRows = useMemo(() => {
		if (view.value === 'details') {
			return []
		}

		const groups: { [key: string]: SummaryRow & { orderIds: Set<number> } } = {}
		rows.forEach((row) => {
			const key = view.value === 'product' ? String(row.idProducts) : row.packingDate
			if (!groups[key]) {
				groups[key] = {
					key,
					label: view.value === 'product' ? row.productName : row.packingDate,
					subLabel: view.value === 'product' ? packSize(row) : '',
					unitName: row.unitName,
					orders: 0,
					noOfPacking: 0,
					quantity: 0,
					totalCost: 0,
					orderIds: new Set<number>(),
				}
			}
			groups[key].orderIds.add(row.idPackingOrders)
			groups[key].orders = groups[key].orderIds.size
			groups[key].noOfPacking += toNumber(row.noOfPacking)
			groups[key].quantity += toNumber(row.quantity)
			groups[key].totalCost += toNumber(row.totalCost)
		})

		return Object.values(groups).sort((a, b) => a.label.localeCompare(b.label))
	}, [rows, view])

	const downloadCsv = () => {
		let header: string[] = []
		let lines: any[][] = []

		if (view.value === 'details') {
			header = ['Date', 'Packing Order No', 'Blending Order No', 'Batch No', 'Project', 'Warehouse', 'Item', 'Product', 'Product Code', 'Pack Size', 'Packing BOM', 'No of Packing', 'Quantity', 'Unit', 'Cost Per Unit', 'Total Cost', 'Created By']
			lines = rows.map((row) => [
				row.packingDate, row.packingOrderNo, row.productionOrderNo, row.batchNo || '', row.projectName, row.warehouseName || '',
				row.itemName, row.productName, row.productCode || '', packSize(row), row.bomName || '',
				toNumber(row.noOfPacking), toNumber(row.quantity), row.unitName, toNumber(row.costPerUnit), toNumber(row.totalCost), row.createdBy,
			])
			lines.push(['Total', '', '', '', '', '', '', '', '', '', '', totals.noOfPacking, totals.quantity, '', '', totals.totalCost, ''])
		} else {
			header = [view.value === 'product' ? 'Product' : 'Date', 'Pack Size', 'Packing Orders', 'No of Packing', 'Quantity', 'Unit', 'Average Cost Per Unit', 'Total Cost']
			lines = summaryRows.map((row) => [
				row.label, row.subLabel, row.orders, row.noOfPacking, row.quantity, view.value === 'product' ? row.unitName : '',
				row.noOfPacking > 0 ? (row.totalCost / row.noOfPacking).toFixed(5) : '', row.totalCost,
			])
			lines.push(['Total', '', totals.orders, totals.noOfPacking, totals.quantity, '', '', totals.totalCost])
		}

		const csv = [header].concat(lines)
			.map((line) => line.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
			.join('\r\n')
		const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' })
		const link = document.createElement('a')
		link.href = URL.createObjectURL(blob)
		link.download = `production-report-${formData.fromDate}-to-${formData.toDate}.csv`
		link.click()
		URL.revokeObjectURL(link.href)
	}

	if (hasPermission !== 1) {
		return null
	}

	return (
		<>
			<Card className='mb-4'>
				<Card.Body>
					<Card.Title className="h5 mb-1">Production Report</Card.Title>
					<p className="text-muted small mb-3">
						Finished goods packed through Packing Orders, with number of packs, quantity (packs × pack size) and packing cost.
					</p>
					<div className='row align-items-end'>
						<div className='col-md-2'>
							<Form.Group className="mb-3" controlId="fromDate">
								<Form.Label>From Date<span className='required'></span></Form.Label>
								<Form.Control type="date" name="fromDate" value={formData.fromDate} onChange={handleChange} />
							</Form.Group>
						</div>
						<div className='col-md-2'>
							<Form.Group className="mb-3" controlId="toDate">
								<Form.Label>To Date<span className='required'></span></Form.Label>
								<Form.Control type="date" name="toDate" value={formData.toDate} onChange={handleChange} />
							</Form.Group>
						</div>
						<div className='col-md-2'>
							<Form.Group className="mb-3" controlId="project">
								<Form.Label>Project</Form.Label>
								<Select
									value={project}
									onChange={(selected: any) => {
										setProject(selected)
										setFormData({ ...formData, idProjects: selected.value })
									}}
									options={projects}
									menuPosition="fixed"
								/>
							</Form.Group>
						</div>
						<div className='col-md-3'>
							<Form.Group className="mb-3" controlId="item">
								<Form.Label>Item</Form.Label>
								<Select
									value={item}
									onChange={(selected: any) => {
										setItem(selected)
										setFormData({ ...formData, idItems: selected.value })
									}}
									options={items}
									menuPosition="fixed"
								/>
							</Form.Group>
						</div>
						<div className='col-md-3'>
							<Form.Group className="mb-3" controlId="warehouse">
								<Form.Label>Finished Goods Warehouse</Form.Label>
								<Select
									value={warehouse}
									onChange={(selected: any) => {
										setWarehouse(selected)
										setFormData({ ...formData, idWarehouses: selected.value })
									}}
									options={warehouses}
									menuPosition="fixed"
								/>
							</Form.Group>
						</div>
					</div>
					<div className='row align-items-end'>
						<div className='col-md-4'>
							<Form.Group className="mb-3" controlId="search">
								<Form.Label>Search</Form.Label>
								<Form.Control
									type="text"
									name="search"
									placeholder="Packing order, blending order, batch or product"
									value={formData.search}
									onChange={handleChange}
								/>
							</Form.Group>
						</div>
						<div className='col-md-3'>
							<Form.Group className="mb-3" controlId="view">
								<Form.Label>View</Form.Label>
								<Select
									value={view}
									onChange={(selected: any) => setView(selected)}
									options={viewOptions}
									menuPosition="fixed"
								/>
							</Form.Group>
						</div>
						<div className='col-md-5'>
							<div className="mb-3">
								<button className='btn btn-sm btn-primary p-3 me-2' onClick={handleViewReport} disabled={loading}>
									{loading && (
										<span className="spinner-border spinner-border-sm me-1"></span>
									)}
									<span><i className='fas fa-eye me-1'></i>View report</span>
								</button>
								<button className="btn btn-sm btn-outline-primary p-3" onClick={downloadCsv} disabled={rows.length === 0}>
									<i className='fas fa-download me-1'></i>Excel (CSV)
								</button>
							</div>
						</div>
					</div>
				</Card.Body>
			</Card>

			{loaded && rows.length > 0 && (
				<div className='row mb-4'>
					{[
						{ label: 'Packing Orders', value: String(totals.orders) },
						{ label: 'No of Packing', value: numberFormat(totals.noOfPacking) },
						{ label: 'Total Quantity', value: numberFormat(totals.quantity) },
						{ label: 'Total Cost', value: numberFormat(totals.totalCost) },
					].map((tile) => (
						<div className='col-md-3 col-6 mb-2' key={tile.label}>
							<div className='border rounded p-3 bg-white'>
								<div className='text-muted small'>{tile.label}</div>
								<div className='fs-4 fw-bolder'>{tile.value}</div>
							</div>
						</div>
					))}
				</div>
			)}

			<div className='row'>
				<div className='col-12'>
					{view.value === 'details' && (
						<Table responsive striped bordered hover size="sm" className="mb-0 bg-white">
							<thead className="table-light">
								<tr>
									<th>SL</th>
									<th>Date</th>
									<th>Packing Order No</th>
									<th>Blending Order No</th>
									<th>Batch No</th>
									<th>Project</th>
									<th>Warehouse</th>
									<th>Product</th>
									<th>Pack Size</th>
									<th>Packing BOM</th>
									<th className="text-end">No of Packing</th>
									<th className="text-end">Quantity</th>
									<th>Unit</th>
									<th className="text-end">Cost / Unit</th>
									<th className="text-end">Total Cost</th>
									<th>Created By</th>
								</tr>
							</thead>
							<tbody>
								{rows.length === 0 && (
									<tr>
										<td colSpan={16} className="text-center text-muted py-4">
											{loaded
												? 'No production found for the selected filters.'
												: 'Choose a date range and click "View report" to load production.'}
										</td>
									</tr>
								)}
								{rows.map((row, index) => (
									<tr key={row.id}>
										<td>{index + 1}</td>
										<td style={{ whiteSpace: 'nowrap' }}>{row.packingDate}</td>
										<td style={{ whiteSpace: 'nowrap' }}>
											<Link to={`/packing-order-details/${row.idPackingOrders}`}>{row.packingOrderNo}</Link>
										</td>
										<td style={{ whiteSpace: 'nowrap' }}>{row.productionOrderNo}</td>
										<td>{row.batchNo}</td>
										<td>{row.projectName}</td>
										<td>{row.warehouseName}</td>
										<td>
											{row.productName}
											{row.productCode ? <span className="text-muted"> ({row.productCode})</span> : null}
										</td>
										<td>{packSize(row)}</td>
										<td>{row.bomName}</td>
										<td className="text-end">{formatDecimalQuantity(row.noOfPacking)}</td>
										<td className="text-end">{numberFormat(row.quantity)}</td>
										<td>{row.unitName}</td>
										<td className="text-end">{numberFormat(row.costPerUnit)}</td>
										<td className="text-end">{numberFormat(row.totalCost)}</td>
										<td>{row.createdBy}</td>
									</tr>
								))}
							</tbody>
							{rows.length > 0 && (
								<tfoot>
									<tr>
										<th colSpan={10} className="text-end">Total</th>
										<th className="text-end">{formatDecimalQuantity(totals.noOfPacking)}</th>
										<th className="text-end">{numberFormat(totals.quantity)}</th>
										<th></th>
										<th></th>
										<th className="text-end">{numberFormat(totals.totalCost)}</th>
										<th></th>
									</tr>
								</tfoot>
							)}
						</Table>
					)}

					{view.value !== 'details' && (
						<Table responsive striped bordered hover size="sm" className="mb-0 bg-white">
							<thead className="table-light">
								<tr>
									<th>SL</th>
									<th>{view.value === 'product' ? 'Product' : 'Date'}</th>
									{view.value === 'product' && <th>Pack Size</th>}
									<th className="text-end">Packing Orders</th>
									<th className="text-end">No of Packing</th>
									<th className="text-end">Quantity</th>
									{view.value === 'product' && <th>Unit</th>}
									<th className="text-end">Avg Cost / Unit</th>
									<th className="text-end">Total Cost</th>
								</tr>
							</thead>
							<tbody>
								{summaryRows.length === 0 && (
									<tr>
										<td colSpan={9} className="text-center text-muted py-4">
											{loaded
												? 'No production found for the selected filters.'
												: 'Choose a date range and click "View report" to load production.'}
										</td>
									</tr>
								)}
								{summaryRows.map((row, index) => (
									<tr key={row.key}>
										<td>{index + 1}</td>
										<td>{row.label}</td>
										{view.value === 'product' && <td>{row.subLabel}</td>}
										<td className="text-end">{row.orders}</td>
										<td className="text-end">{formatDecimalQuantity(row.noOfPacking)}</td>
										<td className="text-end">{numberFormat(row.quantity)}</td>
										{view.value === 'product' && <td>{row.unitName}</td>}
										<td className="text-end">{row.noOfPacking > 0 ? numberFormat(row.totalCost / row.noOfPacking) : ''}</td>
										<td className="text-end">{numberFormat(row.totalCost)}</td>
									</tr>
								))}
							</tbody>
							{summaryRows.length > 0 && (
								<tfoot>
									<tr>
										<th colSpan={view.value === 'product' ? 3 : 2} className="text-end">Total</th>
										<th className="text-end">{totals.orders}</th>
										<th className="text-end">{formatDecimalQuantity(totals.noOfPacking)}</th>
										<th className="text-end">{numberFormat(totals.quantity)}</th>
										{view.value === 'product' && <th></th>}
										<th></th>
										<th className="text-end">{numberFormat(totals.totalCost)}</th>
									</tr>
								</tfoot>
							)}
						</Table>
					)}
				</div>
			</div>
		</>
	)
}

export default PackingProductionReport
